/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {cloneDeep, isEqual, isPlainObject, omit, pick} from 'lodash';

import {VideoDestroyedError} from '@amblique/base/app/plugins/video/errors/video-destroyed';
import {Metadata} from '@amblique/base/app/plugins/video/metadata';

/**
 * Abstract video model
 *
 * @typedef {import('@amblique/base/app/directives/video/models/player').Player} Player
 */
export class AbstractVideo {
  static TYPE = null;
  static PARAMS = [];
  static EVENTS = [];
  static ID_PATTERN;
  static TYPE_PATTERN;

  /**
   * @type {Metadata} Video metadata adapter
   */
  metadata;

  /**
   * @type {Player} Video player adapter
   */
  player;

  /**
   * @type {object}
   */
  position;

  /**
   * @protected
   */
  initialized = false;

  /**
   * @protected
   */
  destroyed = false;

  /**
   * Constructor - ONLY assigns properties (synchronous, passive)
   * No async operations, no side effects, no DOM manipulation
   *
   * @param {HTMLElement} element - Container element
   * @param {object} options - Configuration options
   */
  constructor(element, options) {
    this.setup(element, options);
  }

  /**
   * Setup - Prepare instance state (synchronous)
   * Parse options, setup properties, but don't initialize player
   *
   * @param {HTMLElement} element - Container element
   * @param {object} options - Configuration options
   */
  setup(element, options) {
    const {PARAMS, EVENTS} = this.constructor;

    this.element = element;
    this.id = options.id || options.defaultVideoId;
    this.defaultVideoId = options.defaultVideoId;

    options = Object.assign({clip: 0}, options);
    this.options = omit(options, [...PARAMS, ...EVENTS]);
    this.options.params = pick(options, PARAMS);
    this.options.events = pick(options, EVENTS);
    this.originOptions = cloneDeep(this.options);

    if (options.responsiveOptions) {
      this.setOptions(options.responsiveOptions);
    }

    // Preload metadata if lazy, but don't init player
    if (options.lazy) {
      this.loadMetadata();
    }
  }

  /**
   * @abstract
   * @protected
   */
  get oembedUrl() {
    return null;
  }

  /**
   * Create video player for corresponding video provider
   *
   * @abstract
   * @protected
   *
   * @returns {Player} The video player adapter
   */
  createPlayer(metadata) {}

  /**
   * Initial video
   *
   * @returns {Promise<Player>}
   */
  async init() {
    // If already initialized, return existing player
    if (this.isInitialized()) {
      return Promise.resolve(this.player);
    }

    // If initialization in progress, return existing promise
    if (this.initPromise) {
      return this.initPromise;
    }

    // Start new initialization
    this.initPromise = this
      .loadMetadata()
      .then((metadata) => (this.isDestroyed() ? Promise.reject(new VideoDestroyedError()) : metadata))
      .then((metadata) => this.createPlayer(metadata))
      .then((player) => this.registerEvents(player))
      .then((player) => {
        this.initialized = true;

        return player;
      })
      .catch((error) => {
        this.handleError(error);
        throw error; // Re-throw so callers know init failed
      })
      .finally(() => delete this.initPromise)
    ;

    return this.initPromise;
  }

  /**
   * Destroy the initialized video
   */
  destroy() {
    this.initialized = false;
    this.destroyed = true;

    // Cancel any ongoing operations by clearing promises
    // The isDestroyed() checks in the promise chains will reject them
    delete this.initPromise;
    delete this.metadataPromise;
    delete this.metadata;

    if (this.player) {
      this.player.unload();
      this.player.destroy();

      delete this.player;
    }
  }

  /**
   * Checks if the video instance is initialized or not
   *
   * @returns {boolean} Returns `true` if video is initialized
   */
  isInitialized() {
    return this.initialized;
  }

  /**
   * Checks if the video element removed from DOM or not
   *
   * @returns {boolean} Returns `true` if video element has been removed
   */
  isDestroyed() {
    return this.destroyed;
  }

  /**
   * Update video options
   *
   * @param {object} options New options
   */
  setOptions(options) {
    for (const option in options) {
      const currentValue = this.options[option];
      const value = options[option];

      if (value !== currentValue) {
        this.options[option] = value;

        // Only call player methods if player is initialized
        if (!this.player) {
          continue;
        }

        switch (option) {
          case 'autopause': this.player.setAutopause(!!value).catch(this.handleError); break;
          case 'clip': this.calculateSize(); break;
          case 'color': this.player.setColor(value).catch(this.handleError); break;
          case 'loop': this.player.setLoop(!!value).catch(this.handleError); break;
          case 'mute':
          case 'muted': this.player.setMuted(!!value).catch(this.handleError); break;
          case 'ratio': this.calculateSize(); break;
          case 'speed': this.player.setPlaybackRate(value).catch(this.handleError); break;
          default: console.warn(`Not supported set option: ${option}`);
        }
      }
    }
  }

  /**
   * Load new video by ID
   *
   * @param {string|number} id The video ID to load
   *
   * @returns {Promise<Player>}
   */
  async loadVideo(id) {
    if (id !== this.id && this.isInitialized()) {
      return this.player.isPlaying()
        .then((isPlaying) => {
          const oldVideo = this.id;

          this.id = id;
          delete this.metadata;

          // Update related thumbnails with new ID
          document
            .querySelectorAll(`[data-thumbnail="${oldVideo}"]`)
            .forEach((thumbnail) => (thumbnail.dataset.thumbnail = id))
          ;

          return this.loadMetadata()
            .then((metadata) => (this.isDestroyed() ? Promise.reject(new VideoDestroyedError()) : metadata))
            .then(() => this.player.loadVideo(id))
            .then(() => ((isPlaying || this.options.readonly) ? this.player.play() : null))
            .then(() => this.player)
          ;
        })
        .catch((error) => this.handleError(error))
      ;
    }

    return Promise.resolve(this.player);
  }

  /**
   * Handle video playback errors
   *
   * @param {Error} error The occurred error
   */
  handleError(error) {
    switch (error.name) {
      // The video element has been removed from DOM while initializing. Ignore this error.
      case VideoDestroyedError.NAME: break;

      // Vimeo: If error is `PlayInterrupted`, just print the error message as a warning
      case 'PlayInterrupted': console.warn(error.message); break;

      // Otherwise, log the error to avoid functionality break
      default: console.error(error);
    }
  }

  /**
   * Converts the given params from `boolean` to `number` and vice versa
   *
   * Type: `bool`
   *  - 1 -> `true`
   *  - 0 -> `false`
   *
   * Type: `num`
   *  - `true` -> 1
   *  - `false` -> 0
   *
   * @protected
   *
   * @param {object} params
   * @param {'bool' | 'num'} type
   *
   * @returns {object} The converted params object (new object, not mutated)
   */
  convertParams(params, type = 'num') {
    if (!isPlainObject(params)) {
      return params;
    }

    // Create a new object to avoid mutation
    const converted = {};

    for (const prop in params) {
      if (type === 'num' && typeof params[prop] === 'boolean') {
        converted[prop] = params[prop] ? 1 : 0;
      } else if (type === 'bool' && typeof params[prop] === 'number') {
        converted[prop] = params[prop] === 1 ? true : (params[prop] === 0 ? false : params[prop]);
      } else {
        converted[prop] = params[prop];
      }
    }

    return converted;
  }

  /**
   * Load video metadata
   *
   * @protected
   *
   * @returns {Promise<Metadata>}
   */
  async loadMetadata() {
    // If already loaded, return cached metadata
    if (this.metadata) {
      return Promise.resolve(this.metadata);
    }

    // If loading in progress, return existing promise
    if (this.metadataPromise) {
      return this.metadataPromise;
    }

    // Start loading
    if (!this.isDestroyed()) {
      this.metadataPromise = fetch(this.oembedUrl)
        .then((response) => response.json())
        .then((metadata) => this.onMetadataLoaded(metadata))
        .catch((error) => {
          this.handleError(error);
          throw error; // Re-throw
        })
        .finally(() => delete this.metadataPromise)
      ;

      return this.metadataPromise;
    }

    return Promise.resolve(this.metadata);
  }

  /**
   * @protected
   *
   * @param {object} metadata The video metadata from oembed response
   *
   * @returns {Metadata} The transformed metadata object
   */
  onMetadataLoaded(metadata) {
    if (!this.isDestroyed()) {
      const classList = this.element.classList;

      this.metadata = new Metadata(metadata, {type: this.constructor.TYPE, video_id: this.id});
      this.calculateSize();

      if (this.options.size === 'cover') {
        classList.add('aspect-ratio');
      }

      if (typeof this.options.onMetaLoaded === 'function') {
        this.options.onMetaLoaded(this, this.metadata);
      }

      return this.metadata;
    }
  }

  /**
   * Register video events configured in options
   *
   * @protected
   *
   * @param {Player} player Video player adapter
   *
   * @returns {Player} Video player adapter
   */
  registerEvents(player) {
    if (!this.isDestroyed()) {
      for (const name in this.options.events) {
        if (typeof this.options.events[name] === 'function') {
          player.on(name, (...args) => this.options.events[name](this, ...args));
        }
      }
    }

    return player;
  }

  /**
   * Apply modern CSS clipping/cropping to video element
   *
   * Uses CSS transforms to crop edges by scaling the iframe larger than its container
   * and offsetting it to hide the cropped portions.
   *
   * Benefits of transform over top/left:
   * - GPU-accelerated (better performance)
   * - Sub-pixel precision
   * - No layout reflows
   *
   * @protected
   *
   * @param {HTMLElement} element The iframe or thumbnail element
   */
  applyClip(element) {
    if (this.position && this.options.size === 'cover') {
      element.style.height = (100 / this.position.vertical) + '%';
      element.style.top = -(100 / this.position.vertical * this.position.top) + '%';

      element.style.width = (100 / this.position.horizontal) + '%';
      element.style.left = -(100 / this.position.horizontal * this.position.left) + '%';
    }
  }

  /**
   * Set video position and apply aspect ratio
   *
   * Modern implementation using native CSS aspect-ratio instead of padding-bottom hack.
   * Maintains backward compatibility by storing position data and applying orientation classes.
   *
   * @protected
   *
   * @param {object} position Position object with crop/clip data
   * @param {number} position.horizontal - Horizontal visible portion (0-1)
   * @param {number} position.vertical - Vertical visible portion (0-1)
   * @param {number} position.top - Top crop amount (0-1)
   * @param {number} position.left - Left crop amount (0-1)
   * @param {number} position.right - Right crop amount (0-1)
   * @param {number} position.bottom - Bottom crop amount (0-1)
   * @param {number} position.aspectRatio - Aspect ratio (width/height)
   */
  setPosition(position) {
    if (isEqual(this.position, position)) {
      return;
    }

    const classList = this.element.classList;

    this.position = position;

    // Use modern aspect-ratio CSS property
    this.element.style.setProperty('aspect-ratio', `${position.aspectRatio}`);

    // Maintain orientation classes for backward compatibility and styling hooks
    // Square: 1:1, Landscape: wider than tall, Portrait: taller than wide
    classList.toggle('square', position.aspectRatio === 1);
    classList.toggle('landscape', position.aspectRatio > 1);
    classList.toggle('portrait', position.aspectRatio < 1);

    // Apply clipping to iframes and thumbnails when using 'cover' mode
    if (this.options.size === 'cover') {
      this.element
        .querySelectorAll('iframe, [data-thumbnail]')
        .forEach((element) => this.applyClip(element))
      ;
    }
  }

  /**
   * Calculate video size, aspect ratio, and clipping based on options
   *
   * This method processes the `ratio` and `clip` options to determine how the video
   * should be displayed and cropped. It supports:
   *
   * - **Custom Aspect Ratio**: `ratio` option overrides video's native aspect ratio
   * - **Edge Cropping**: `clip` option crops edges (CSS margin-like syntax)
   *
   * @protected
   *
   * @example
   * // Square crop (1:1) - auto-calculates clip to achieve ratio
   * { ratio: 1 }
   *
   * @example
   * // Crop 10% from all edges
   * { clip: 0.1 }
   *
   * @example
   * // Crop 10% top/bottom, 20% left/right
   * { clip: "0.1 0.2" }
   *
   * @example
   * // Crop 10% top, 5% left/right, 15% bottom
   * { clip: "0.1 0.05 0.15" }
   *
   * @example
   * // Crop each edge individually (clockwise: top, right, bottom, left)
   * { clip: "0.1 0.05 0.15 0.2" }
   */
  calculateSize() {
    // Can't calculate video size if video metadata is not loaded
    if (!this.metadata) {
      return;
    }

    const {ratio, width, height} = this.metadata;
    const originRatio = ratio || (width / height);

    // If `ratio` is set in options, it'll take precedence over `clip` option.
    if (this.options.ratio) {
      if (this.options.ratio > originRatio) {
        const customHeight = width / this.options.ratio;

        this.options.clip = `${(height - customHeight) / 2 / height} 0`;
      } else if (this.options.ratio < originRatio) {
        const customWidth = height * this.options.ratio;

        this.options.clip = `0 ${(width - customWidth) / 2 / width}`;
      } else {
        this.options.clip = 0;
      }
    }

    // Calculate video size based on configured `clip`
    const position = {horizontal: 1, vertical: 1, top: 0, left: 0};

    if (Number.isFinite(this.options.clip) || this.options.clip) {
      let edges = this.options.clip;
      let top, right, bottom, left;

      if (Number.isFinite(edges)) {
        edges = [edges];
      } else if (typeof edges === 'string') {
        edges = edges.replace(/\s+/, ' ').split(' ');
      }

      if (Array.isArray(edges)) {
        edges = edges.map(parseFloat);

        if (edges.length === 1) {
          top = right = bottom = left = edges[0];
        } else if (edges.length === 2) {
          ([top, right] = edges);

          bottom = top;
          left = right;
        } else if (edges.length === 3) {
          ([top, right, bottom] = edges);

          left = right;
        } else if (edges.length === 4) {
          [top, right, bottom, left] = edges;
        } else {
          console.warn(`Invalid video "clip" option with value "${this.options.clip}".`);
        }
      }

      if (edges.filter(Number.isFinite).length === edges.length) {
        Object.assign(position, {top, right, bottom, left});

        position.horizontal = 1 - left - right;
        position.vertical = 1 - top - bottom;
      }
    }

    // Calculate the final aspect ratio
    position.aspectRatio = originRatio * (position.horizontal / position.vertical);

    this.setPosition(position);
  }
}
